import { useState, useEffect } from 'react';
import { Button, Spinner } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import Header from '../../components/layout/Header';
import Body from '../../components/layout/Body';
import Tabs from '../../components/layout/Tabs';
import OptionPageSettings from '../components/OptionPageSettings';
import FieldSettings from '../components/FieldSettings';
import { useNotice } from '../../contexts/NoticeContext';
import { useURLParams } from '../../contexts/ParamsContext';
import { fetchOptionPage, saveOptionPage } from '../../utils/api';

const OptionPageEdit = () => {
    const [isSaving, setIsSaving] = useState(false);
    const { params, updateUrlParams } = useURLParams();
    const [pageData, setPageData] = useState({ name: '', fields: [] });
    const { showNotice } = useNotice();

    const handleSave = () => {
        setIsSaving(true);
        saveOptionPage(pageData)
            .then((savedPage) => {
                const savedId = savedPage?.data?.id;
                const fieldIds = savedPage?.data?.field_ids || [];

                setPageData(prev => ({
                    ...prev,
                    id: savedId,
                    fields: prev.fields.map((field, index) => ({
                        ...field,
                        id: field.id || fieldIds[index]
                    }))
                }));

                updateUrlParams({ view: 'edit-options-page', id: savedId });

                setTimeout(() => {
                    showNotice(
                        __(savedPage?.message || 'Updated successfully.', 'voxycure-framework'),
                        'success'
                    );
                }, 50);
            })
            .catch((error) => {
                showNotice(
                    error?.message || __('Something went wrong while saving option page.', 'voxycure-framework'),
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const handleFieldsChange = (updatedFields) => {
        setPageData({ ...pageData, fields: updatedFields });
    };

    useEffect(() => {
        if (!params.id) return;
        fetchOptionPage(params.id)
            .then((data) => setPageData(data))
            .catch((error) => {
                showNotice(
                    error?.message || __('Something went wrong while fetching option page.', 'voxycure-framework'),
                    'error'
                );
            });
    }, []);

    const tabList = [
        { name: 'page-settings', title: __('Page Settings', 'voxycure-framework'), className: 'page-settings-tab' },
        { name: 'fields', title: __('Fields', 'voxycure-framework'), className: 'fields-tab' },
    ];

    return (
        <>
            <Header>
                <h2 className='voxycure-editor__header-title'>
                    {pageData.id
                        ? __('Edit Options Page', 'voxycure-framework')
                        : __('Add New Options Page', 'voxycure-framework')}
                </h2>
                <Button
                    className='components-button is-primary voxycure-button'
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <Spinner style={{ verticalAlign: 'middle', marginRight: 6 }} size={16} />
                            {__('Saving...', 'voxycure-framework')}
                        </>
                    ) : (
                        __('Save', 'voxycure-framework')
                    )}
                </Button>
            </Header>

            <Body>
                <Tabs
                    tabs={tabList}
                    renderTab={(tab) => {
                        switch (tab.name) {
                            case 'page-settings':
                                return <OptionPageSettings pageData={pageData} setPageData={setPageData} />;
                            case 'fields':
                                return <FieldSettings fields={pageData.fields || []} tabs={pageData.tabs || []} onFieldsChange={handleFieldsChange} />;
                            default:
                                return null;
                        }
                    }}
                />
            </Body>
        </>
    );
};

export default OptionPageEdit;
